import { z } from 'zod';

// Param schemas
export const orgTagIdParam = z.object({
  id: z.string().uuid(),
});

// Query schemas
export const listOrgTagsQuery = z.object({
  organization_id: z.string().uuid(),
});

// The tag slug's grammar, exported because it is enforced at three independent
// points — this schema, OrgDO's own create-path guard, and the web dialog's
// slug derivation — and a drifting copy would let one of them accept a name the
// next one rejects.
export const ORG_TAG_NAME_REGEX = /^[a-z0-9]+(-[a-z0-9]+)*$/;
export const ORG_TAG_NAME_MAX_LENGTH = 50;

// Body schemas
export const createOrgTagBody = z.object({
  organization_id: z.string().uuid(),
  name: z.string().min(1).max(ORG_TAG_NAME_MAX_LENGTH).regex(ORG_TAG_NAME_REGEX, {
    message: 'Must be lowercase alphanumeric with hyphens (e.g. "my-tag")',
  }),
  display_name: z.string().max(100).nullish(),
  color: z.string().max(20).nullish(),
});

// `name` is deliberately absent: the slug is immutable after creation. It is the
// wire identity that `hub.yaml` `tags:` lists and CLI `--tag` flags resolve
// against, and nothing propagates a rename to those references. Relabel via
// `display_name`. A stale client's `name` is stripped by this schema (non-strict
// object) rather than rejected — silent, and accepted as such.
export const updateOrgTagBody = z.object({
  // Required so the route targets the org the UI is managing (which may differ
  // from the token's default `auth.org_id` for multi-org users) — mirrors create/list.
  organization_id: z.string().uuid(),
  display_name: z.string().max(100).nullish(),
  color: z.string().max(20).nullish(),
});

// Inferred types
export type CreateOrgTagInput = z.infer<typeof createOrgTagBody>;
export type UpdateOrgTagInput = z.infer<typeof updateOrgTagBody>;

// =============================================================================
// RESPONSE TYPES
// =============================================================================

export interface OrgTagResponse {
  tag_id: string;
  name: string;
  display_name: string | null;
  color: string | null;
  created_at: string;
  updated_at: string;
}

export interface ListOrgTagsResponse {
  data: OrgTagResponse[];
}

export interface CreateOrgTagResponse {
  data: OrgTagResponse;
}

export interface UpdateOrgTagResponse {
  data: OrgTagResponse;
}
